// frontend/src/components/UserCard/UserCard.js
import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  alpha,
  useTheme
} from '@mui/material';
import {
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const IOS_RADIUS = 4;
const GLASS_BORDER = 'rgba(255,255,255,0.62)';

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme.spacing(IOS_RADIUS),
  border: `1px solid ${alpha(theme.palette.common.white, 0.58)}`,
  background: `
    linear-gradient(145deg,
      ${alpha(theme.palette.background.paper, 0.88)} 0%,
      ${alpha(theme.palette.background.paper, 0.68)} 100%)
  `,
  backdropFilter: 'blur(24px) saturate(155%)',
  WebkitBackdropFilter: 'blur(24px) saturate(155%)',
  boxShadow: `
    0 18px 45px ${alpha(theme.palette.common.black, 0.07)},
    inset 0 1px 0 ${GLASS_BORDER}
  `,
  height: '100%',
  minHeight: 380,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.common.white, 0.95)}, transparent)`,
    pointerEvents: 'none'
  },
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: alpha(theme.palette.primary.main, 0.28),
    boxShadow: `
      0 24px 55px ${alpha(theme.palette.common.black, 0.11)},
      0 0 0 1px ${alpha(theme.palette.primary.main, 0.06)},
      inset 0 1px 0 ${GLASS_BORDER}
    `
  },
  '&:active': {
    transform: 'translateY(-1px) scale(0.995)'
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': {
      transform: 'none'
    }
  }
}));

const CardHeader = styled(Box)(({ theme, variant }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  background: variant === 'recommendation'
    ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.11)}, ${alpha(theme.palette.primary.main, 0.035)})`
    : `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.46)}, ${alpha(theme.palette.action.hover, 0.28)})`,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexShrink: 0
}));

const StyledAvatar = styled(Avatar)(({ theme, variant }) => ({
  width: 64,
  height: 64,
  flexShrink: 0,
  backgroundColor: `${alpha(theme.palette.background.paper, 0.86)} !important`,
  border: `1.5px solid ${
    variant === 'recommendation'
      ? alpha(theme.palette.primary.main, 0.32)
      : alpha(theme.palette.text.primary, 0.10)
  }`,
  color: variant === 'recommendation'
    ? theme.palette.primary.main
    : theme.palette.text.primary,
  fontSize: '1.35rem',
  fontWeight: 700,
  boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.07)}`,
  '&.MuiAvatar-colorDefault': {
    backgroundColor: `${alpha(theme.palette.background.paper, 0.86)} !important`
  }
}));

const InviteButton = styled(Button)(({ theme, variant }) => ({
  minHeight: 40,
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(0.75, 1.75),
  textTransform: 'none',
  fontSize: '0.82rem',
  fontWeight: 650,
  letterSpacing: '-0.01em',
  boxShadow: 'none',
  ...(variant === 'recommendation'
    ? {
        background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          background: `linear-gradient(180deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
          boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.22)}`
        }
      }
    : {
        background: alpha(theme.palette.text.primary, 0.075),
        color: theme.palette.text.primary,
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        '&:hover': {
          background: alpha(theme.palette.text.primary, 0.12),
          boxShadow: `0 8px 20px ${alpha(theme.palette.common.black, 0.08)}`
        }
      })
}));

const GlassChip = styled(Chip)(({ theme, tone = 'primary' }) => {
  const color = tone === 'interest'
    ? theme.palette.secondary.main
    : theme.palette.primary.main;

  return {
    height: 24,
    borderRadius: theme.spacing(1.5),
    fontSize: '0.67rem',
    fontWeight: 600,
    background: alpha(color, 0.08),
    color,
    border: `1px solid ${alpha(color, 0.12)}`,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1)
    }
  };
});

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1.5),
  marginTop: theme.spacing(0.5),
  borderRadius: theme.spacing(2),
  background: alpha(theme.palette.text.primary, 0.035),
  border: `1px solid ${alpha(theme.palette.text.primary, 0.055)}`,
  color: theme.palette.text.disabled
}));

const MatchBar = styled(Box)(({ theme }) => ({
  height: 6,
  borderRadius: 999,
  overflow: 'hidden',
  background: alpha(theme.palette.primary.main, 0.09)
}));

const MatchFill = styled(Box)(({ theme, value }) => ({
  width: `${Math.max(0, Math.min(100, value))}%`,
  height: '100%',
  borderRadius: 999,
  background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
  transition: 'width 400ms ease'
}));

const getInitials = (user) => {
  if (!user) return '?';
  const firstName = user.first_name || '';
  const lastName = user.last_name || '';
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName[0].toUpperCase();
  if (user.email) return user.email[0].toUpperCase();
  return '?';
};

const getFullName = (user) => {
  if (!user) return 'Пользователь';
  if (user.full_name) return user.full_name;
  const parts = [];
  if (user.last_name) parts.push(user.last_name);
  if (user.first_name) parts.push(user.first_name);
  if (user.middle_name) parts.push(user.middle_name);
  return parts.length > 0 ? parts.join(' ') : user.email || 'Пользователь';
};

const getPosition = (user) => {
  if (!user) return 'Не указано';
  if (user.position) {
    const positionMap = {
      professor: 'Профессор',
      researcher: 'Исследователь',
      teacher: 'Преподаватель',
      student: 'Студент',
      lecturer: 'Преподаватель'
    };
    return positionMap[user.position] || user.position;
  }
  return 'Не указано';
};

const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  return `https://fincollab.ru${avatar}`;
};

const UserCard = ({ user, onInvite, variant = 'simple' }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/profile/${user.id}`);
  };

  const handleInviteClick = (e) => {
    e.stopPropagation();
    if (onInvite) onInvite(user);
  };

  const matchScore = user.match_score ? Math.round(user.match_score * 100) : 0;
  const avatarUrl = getAvatarUrl(user.avatar || user.avatar_url);

  const hasCompetencies = Array.isArray(user.competencies) && user.competencies.length > 0;
  const hasInterests = Array.isArray(user.research_interests) && user.research_interests.length > 0;

  return (
    <StyledCard onClick={handleCardClick}>
      <CardHeader variant={variant}>
        <StyledAvatar src={avatarUrl} variant={variant}>
          {!avatarUrl && getInitials(user)}
        </StyledAvatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ letterSpacing: '-0.02em', lineHeight: 1.25 }}
          >
            {getFullName(user)}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ mt: 0.35, fontSize: '0.78rem' }}
          >
            {getPosition(user)}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.55, minWidth: 0 }}>
            <LocationOnIcon sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
              {user.branch || user.university || 'Финансовый университет'}
            </Typography>
          </Box>
        </Box>
      </CardHeader>

      <CardContent
        sx={{
          px: 2,
          py: 1.75,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        {variant === 'recommendation' && (
          <Box
            sx={{
              mb: 1.75,
              p: 1.25,
              borderRadius: 2.5,
              background: alpha(theme.palette.primary.main, 0.045),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.7 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Совместимость
              </Typography>
              <Typography variant="caption" fontWeight={750} color="primary">
                {matchScore}%
              </Typography>
            </Box>
            <MatchBar>
              <MatchFill value={matchScore} />
            </MatchBar>
          </Box>
        )}

        {hasCompetencies ? (
          <Box sx={{ mb: hasInterests ? 1.5 : 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={650}
              sx={{ display: 'block', mb: 0.7 }}
            >
              Навыки
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
              {user.competencies.slice(0, 3).map((skill, i) => (
                <GlassChip
                  key={`${skill}-${i}`}
                  label={skill.length > 14 ? `${skill.substring(0, 12)}...` : skill}
                  size="small"
                />
              ))}
              {user.competencies.length > 3 && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '24px', fontWeight: 600 }}>
                  +{user.competencies.length - 3}
                </Typography>
              )}
            </Box>
          </Box>
        ) : variant === 'simple' ? (
          <EmptyState>
            <CodeIcon sx={{ fontSize: 20, opacity: 0.45 }} />
            <Typography variant="caption" color="text.disabled">
              Навыки не указаны
            </Typography>
          </EmptyState>
        ) : null}

        {hasInterests && (
          <Box sx={{ mt: 'auto' }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={650}
              sx={{ display: 'block', mb: 0.7 }}
            >
              Научные интересы
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
              {user.research_interests.slice(0, 2).map((interest, i) => (
                <GlassChip
                  key={`${interest}-${i}`}
                  tone="interest"
                  label={interest.length > 17 ? `${interest.substring(0, 15)}...` : interest}
                  size="small"
                />
              ))}
              {user.research_interests.length > 2 && (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '24px', fontWeight: 600 }}>
                  +{user.research_interests.length - 2}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0, mt: 'auto' }}>
        <InviteButton
          fullWidth
          variant={variant}
          onClick={handleInviteClick}
          startIcon={<EmailIcon sx={{ fontSize: 18 }} />}
        >
          Пригласить
        </InviteButton>
      </CardActions>
    </StyledCard>
  );
};

export default UserCard;
